import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtMoney } from "@/lib/payments";
import { Coins, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// 400 « Crédit publicitaire insuffisant » (err.ad_credit_insufficient, 4 languages) → toast with a « Recharger » action.
export const isInsufficientCredit = (e) => e?.response?.status === 400 && /cr[ée]dit/i.test(String(e?.friendlyMessage || e?.response?.data?.detail || ""));
export const toastCreditError = (e, t, nav, fallback) => {
  const msg = e?.friendlyMessage || fallback || t("common.error", "Erreur");
  if (isInsufficientCredit(e)) toast.error(msg, { action: { label: t("adcredit.topup", "Recharger"), onClick: () => nav("/ads/credits") }, duration: 8000 });
  else toast.error(msg);
};

// Ad-credit wallet (separate from the donation wallet of creators): balance + packs.
export const useAdCredits = (enabled = true) => {
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    return api.get("/payments/ad-credits/mine").then(({ data: d }) => setData(d)).catch(() => setData({ balance: 0, transactions: [] }));
  }, [enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { data, balance: Number(data?.balance || 0), currency: data?.currency || "EUR", reload };
};

// Compact balance line shown wherever a member prepares a promotion.
// `need` = budget about to be spent → highlights an insufficient balance.
export const AdCreditBalance = ({ balance, currency, need = 0, loading = false, onBeforeNavigate, className = "" }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const short = Number(need) > 0 && Number(need) > Number(balance || 0);
  return (
    <div
      className={`rounded-xl px-3 py-2.5 flex items-center gap-2 text-sm ${className}`}
      style={{ background: short ? "rgba(245,158,11,0.12)" : "var(--yalola-surface-alt)", border: `1px solid ${short ? "rgba(245,158,11,0.5)" : "var(--yalola-border)"}` }}
      data-testid="ad-credit-balance"
    >
      {short ? <AlertTriangle size={16} style={{ color: "#b45309" }} /> : <Coins size={16} style={{ color: "var(--yalola-primary)" }} />}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{t("adcredit.balance", "Crédits publicitaires")}</div>
        <div className="font-extrabold tabular-nums" data-testid="ad-credit-balance-value">{loading ? "…" : fmtMoney(balance, currency, lang)}</div>
        {short && (
          <div className="text-[11px]" style={{ color: "#b45309" }} data-testid="ad-credit-insufficient">
            {t("adcredit.insufficient", "Solde insuffisant : il manque {amount}").replace("{amount}", fmtMoney(Number(need) - Number(balance || 0), currency, lang))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => { onBeforeNavigate?.(); nav("/ads/credits"); }}
        className="pill pill-primary text-xs shrink-0 inline-flex items-center gap-1"
        data-testid="ad-credit-topup"
      >
        <Plus size={13} /> {t("adcredit.topup", "Recharger")}
      </button>
    </div>
  );
};
